/**
 * Labstep
 *
 * @module grid/BatchCreateResourceItems/coldefs/resourceItem
 * @desc Column Definition for resource item name
 */

import type {
  EditableCallbackParams,
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-community';
import type { ResourceItemData } from 'labstep-web/components/ResourceItem/Action/BulkCreate/Modal/type';
import { ICONS } from 'labstep-web/constants/icons';
import { suppressKeyboardEventClearCells } from 'labstep-web/core/DataGrid/CellEditor/utils';
import { DataGridPlaceholder } from 'labstep-web/core/DataGrid/Placeholder';
import type { CellRendererParams } from 'labstep-web/core/DataGrid/types';
import { Flex } from 'labstep-web/core/Flex';
import { Icon } from 'labstep-web/core/Icon';
import { GridBatchCreateResourceItemsService } from 'labstep-web/grid/BatchCreateResourceItems/services/grid-batch-create-resource-items-service';
import type { BatchCreateResourceItemsColDef } from 'labstep-web/grid/BatchCreateResourceItems/types';

export const getResourceItemColDef = (
  data: ResourceItemData[],
  setData: React.Dispatch<React.SetStateAction<ResourceItemData[]>>,
  resource?: any,
): BatchCreateResourceItemsColDef => {
  return {
    colId: 'name',
    headerName: 'Resource Item Name',
    headerComponentParams: {
      icon: ICONS.resource_item.primary,
      info: 'Click "Add item" to automatically generate a name with an incrementing number (e.g., Ethanol #1, Ethanol #2), or type a custom name.',
    },
    editable: (params: EditableCallbackParams) => true,
    width: 300,
    valueGetter: (params: ValueGetterParams) => {
      return params.node?.data?.name || '';
    },
    valueSetter: (params: ValueSetterParams) => {
      if (!params.node) {
        return false;
      }

      const row = params.node.data;
      row.name = params.newValue;
      data[params.node.rowIndex!] = row;
      setData([...data]);

      GridBatchCreateResourceItemsService.removeErrorInData(
        data,
        setData,
        params,
      );
      return true;
    },
    cellRenderer: (params: CellRendererParams<ResourceItemData>) => {
      if (!params.node) {
        return null;
      }

      const err = GridBatchCreateResourceItemsService.findErrorInData(
        data,
        params,
      );
      const row = params.node.data;
      if (row.name) {
        return (
          <Flex spacing="between">
            <span>{row.name}</span>
            {err && (
              <Icon
                color={'red'}
                name="warning sign"
                popup={{
                  content: err.message,
                }}
              />
            )}
          </Flex>
        );
      }

      return (
        <Flex spacing="between">
          <DataGridPlaceholder
            params={params}
            editable={true}
            children={'Add item'}
          />

          {err && (
            <Icon
              color={'red'}
              name="warning sign"
              popup={{
                content: err.message,
              }}
            />
          )}
        </Flex>
      );
    },
    suppressKeyboardEvent: suppressKeyboardEventClearCells,
    valueClearer: () => null,
    cellStyle: (params) => {
      if (!params.node) {
        return null;
      }

      const err = GridBatchCreateResourceItemsService.findErrorInData(
        data,
        params,
      );
      if (err) {
        return { backgroundColor: '#F5B1B1' };
      }

      return null;
    },
  };
};
